import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import HeaderRedirect from '../../core/HeaderRedirect';
import type { RootState } from '../../ApplicationState';
import { useAuth } from '../../CustomHooks';
import { logger } from '../../error-tracker';
import { LoginState } from '../../UserManagement';
import { isLikelyNetworkTimeoutError } from '../../UserManagementHelpers';
import { useErrorStatus } from '../EnableErrorMessagePopup/EnableErrorMessagePopup';
import UnauthenticatedHeader from '../UnauthenticatedHeader/UnauthenticatedHeader';

export const SmartHeader = (props) => {
  // console.debug(`%cSmartHeader render`, 'background: pink; color: black');
  const auth = useAuth();
  const onShowErrorMessage = useErrorStatus();
  const history = useHistory();
  const location = useLocation();
  // Typed so the switch below is checked for exhaustiveness. Previously this
  // selector was implicitly `any`, which is why a login state could be added
  // without the compiler noticing it had no header.
  const loginState = useSelector((state: RootState) => state.user.loginState);

  useEffect(() => {
    // console.debug(
    //   `%cCHECK if login state is unknown`,
    //   'background: pink; color: black'
    // );
    if (auth.isUserLoggedInStateUnknown()) {
      void auth.reactivateUserSession().catch((error: unknown) => {
        if (isLikelyNetworkTimeoutError(error) && typeof onShowErrorMessage === 'function') {
          onShowErrorMessage(
            'We are having trouble reaching Peekapak right now. Please check your connection and try again.',
            'Connection timed out',
          );
          return;
        }

        console.error('Cannot reactivate user session from SmartHeader:', error);
      });
    }
  }, [auth, onShowErrorMessage]);

  switch (loginState) {
    case LoginState.loggedIn:
      return <HeaderRedirect history={history} location={location} isHidePeekaville />;

    //
    // A Cognito identity with no Peekapak profile is not a Peekapak user yet,
    // so it gets the same header as a signed-out visitor. LOGIN_PARTIAL used to
    // fall through to `null` below, which left an OAuth user with no Peekapak
    // account stranded on /trial with no header and therefore no Login link -
    // and because the Cognito tokens live in cookieStorage, reactivation put
    // them straight back into LOGIN_PARTIAL on every reload. Clearing cookies
    // was the only way out (PE-1810).
    //
    case LoginState.partial:
    case LoginState.loggedOut:
    case LoginState.expiredSession:
      return <UnauthenticatedHeader {...props} />;

    //
    // Reactivation is still in flight and resolves into one of the states above
    // within a request or two. Rendering the unauthenticated header here would
    // flash "Login" at someone who is signing in perfectly well.
    //
    case LoginState.unknown:
    case LoginState.reactivating:
    case LoginState.authenticated:
      return null;

    // Parent storybook access is served inside its own chrome.
    case LoginState.storybookAccess:
      return null;

    default: {
      // Compile-time guarantee that every LoginState above is handled; at run
      // time a new one degrades to the visitor header rather than to no header.
      const unhandled: never = loginState;
      logger.logException(new Error(`SmartHeader: unhandled LoginState ${String(unhandled)}`));
      return <UnauthenticatedHeader {...props} />;
    }
  }
};
